import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { registerUserAndInitiateOtp } from '../services/authService';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/Input';
import Button from '../components/Button';

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
`;

const FormContainer = styled.div`
  width: 100%;
`;


const Row = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0;
  }
`;

const InfoText = styled.div`
  margin-top: 24px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
`;

const StyledLink = styled(Link)`
  color: black;
  text-decoration: underline;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;
const ErrorMessage = styled.p`
  color: #e53935;
  margin-bottom: 15px;
  margin-top: 10px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
`;

const RegisterScreen: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setError('');

    switch (name) {
      case 'firstName': setFirstName(value); break;
      case 'lastName': setLastName(value); break;
      case 'email': setEmail(value); break;
      case 'password': setPassword(value); break;
    }
  };

  const handleRegister = async () => {
    setError('');
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill all required fields.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address'); return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long'); return;
    }

    setLoading(true);
    try {
      const result = await registerUserAndInitiateOtp({
        firstName, lastName, email, password
      });

      if (result && result.userId) {
        navigate('/verify-email', {
            state: { userId: result.userId, email }
        });
      } else {
        setError('Registration completed, but failed to initiate verification. Please contact support.');
        console.error("Registration succeeded but failed to get userId or initiate OTPs");
      }

    } catch (error: any) {
      console.error('Registration or OTP initiation error:', error);
      if (error.message.includes('Email already in use')) {
        setError('This email address is already registered.');
      } else if (error.message.includes('OTP_SEND_FAILED') || error.message.includes('Failed to send')) {
        setError('Failed to send verification code. Check details and try again.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Register</title>
        <meta name="description" content={`${APP_NAME} - Register`} />
      </Helmet>
      <AuthLayout imageUrl="/assets/register-image.jpg" altText="Create your account">
      <Title>Register</Title>
      <FormContainer>
        <Row>
          <Input type='text' name="firstName" value={firstName} onChange={handleInputChange} placeholder="First name" label="First Name" required />
          <Input type='text' name="lastName" value={lastName} onChange={handleInputChange} placeholder="Last name" label="Last Name" required />
        </Row>
        <Input name="email" type="email" value={email} onChange={handleInputChange} placeholder="Enter your email" label="Email Address" required />
        <Input name="password" type="password" value={password} onChange={handleInputChange} placeholder="Create password (min 6 chars)" label="Password" required />

        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Button text="Create Account" onClick={handleRegister} loading={loading} disabled={loading || !firstName || !lastName || !email || !password} width="100%" />
      </FormContainer>      <InfoText>Already have an account? <StyledLink to="/login">Sign in</StyledLink></InfoText>
    </AuthLayout>
    </>
  );
};

export default RegisterScreen;